import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Barbell, TrendUp, ChartLine } from "@phosphor-icons/react";

/**
 * Per-exercise progress chart.
 * Props: fetchPath = "/client/progress/exercises" OR "/coach/clients/{id}/progress/exercises"
 */
export default function ExerciseProgress({ fetchPath }) {
  const [data, setData] = useState({ exercises: [] });
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(fetchPath);
        setData(data || { exercises: [] });
        if ((data?.exercises || []).length) setActive(data.exercises[0].name);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchPath]);

  if (loading) return <div className="id-card"><div className="id-label text-zinc-500 id-pulse">— Loading —</div></div>;
  if (err) return <div className="id-card border-[#FF3333] text-[#FF3333] text-sm">{err}</div>;

  const exercises = data.exercises || [];
  if (exercises.length === 0) {
    return (
      <div className="id-card" data-testid="exercise-progress-empty">
        <div className="flex items-center gap-2 mb-3">
          <Barbell size={16} weight="bold" className="text-[#FF3333]" />
          <div className="id-label">Lifting progress</div>
        </div>
        <p className="text-zinc-500 font-manrope text-sm">
          Log a workout from the Program page to start tracking your lifts here.
        </p>
      </div>
    );
  }

  const cur = exercises.find((e) => e.name === active) || exercises[0];
  const chartData = (cur.points || []).map((p) => ({
    date: new Date(p.date).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    "Top set (kg)": p.weight,
    "Est. 1RM (kg)": p.est_1rm,
  }));

  return (
    <div className="id-card" data-testid="exercise-progress">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChartLine size={16} weight="bold" className="text-[#FF3333]" />
          <div className="id-label">Lifting progress</div>
        </div>
        <div className="text-[10px] text-zinc-500 font-manrope uppercase tracking-[0.15em]">
          {exercises.length} exercise{exercises.length !== 1 ? "s" : ""} tracked
        </div>
      </div>

      {/* Exercise picker */}
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="exercise-picker">
        {exercises.map((e) => (
          <button
            key={e.name}
            onClick={() => setActive(e.name)}
            data-testid={`exercise-tab-${e.name.replace(/\s+/g, "-")}`}
            className={`px-3 py-1.5 border uppercase tracking-[0.1em] text-[10px] font-bold transition-colors ${
              cur.name === e.name
                ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                : "border-[#262626] text-zinc-400 hover:text-white"
            }`}
          >
            {e.name}
          </button>
        ))}
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <Stat label="Sessions" value={cur.sessions} />
        <Stat label="Current 1RM" value={`${cur.current_est_1rm} kg`} />
        <Stat
          label="Δ Since start"
          value={`${cur.delta_est_1rm > 0 ? "+" : ""}${cur.delta_est_1rm} kg`}
          accent={cur.delta_est_1rm > 0}
          neg={cur.delta_est_1rm < 0}
        />
      </div>

      {/* Chart */}
      <div className="h-56" data-testid="exercise-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 12, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} axisLine={{ stroke: "#262626" }} tickLine={false} />
            <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={{ stroke: "#262626" }} tickLine={false} />
            <Tooltip
              contentStyle={{ background: "#0F0F0F", border: "1px solid #FF3333", fontFamily: "Manrope" }}
              labelStyle={{ color: "#FF3333", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 }}
              itemStyle={{ color: "#E5E5E5" }}
            />
            <Line type="monotone" dataKey="Top set (kg)" stroke="#FF3333" strokeWidth={2.5} dot={{ fill: "#FF3333", r: 3 }} />
            <Line type="monotone" dataKey="Est. 1RM (kg)" stroke="#FFA500" strokeWidth={1.5} strokeDasharray="3 3" dot={{ fill: "#FFA500", r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-zinc-500 font-manrope mt-2 leading-snug">
        Solid red = heaviest set logged · dashed orange = estimated 1-rep max (Epley formula).
      </div>
    </div>
  );
}

function Stat({ label, value, accent, neg }) {
  const color = accent ? "text-emerald-400" : neg ? "text-[#FF3333]" : "text-white";
  return (
    <div className="border border-[#262626] bg-[#0F0F0F] px-3 py-2">
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">{label}</div>
      <div className={`font-oswald text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
